import type { Gate, Role } from '@/data/permissions';
import type { Member } from '@/data/types';

/**
 * The fixed team rules of contract §3.5, mirrored so the Team screen can say WHY a control is off
 * (design 5.1). Courtesy only: member_set_role / member_set_status re-check every one.
 *
 * I1: only an admin grants admin or manager. I2: a non-admin changes dispatchers and accounting only,
 * never himself. I3: the agency always keeps one active admin.
 */

/** A reason a control is disabled: an i18n key, plus values that are themselves i18n keys. */
export interface BlockReason {
  key: string;
  values?: Record<string, string | number>;
  keyValues?: Record<string, string>;
}

const ELEVATED: readonly Role[] = ['admin', 'manager'];

/** The roles a caller may give (I1): all four for an admin, dispatcher and accounting otherwise. */
export function grantableRoles(callerRole: Role): Role[] {
  return callerRole === 'admin' ? ['admin', 'manager', 'dispatcher', 'accounting'] : ['dispatcher', 'accounting'];
}

export function activeAdminCount(members: readonly Member[]): number {
  return members.filter((m) => m.role === 'admin' && m.status === 'active').length;
}

export type MemberAction = 'setRole' | 'deactivate' | 'reactivate';

export interface MemberBlockInput {
  callerRole: Role;
  can: (gate: Gate) => boolean;
  target: Member;
  action: MemberAction;
  /** The role picked, for `setRole`. */
  newRole?: Role;
  members: readonly Member[];
}

/** null when the action is allowed, else the first reason, in the order: permission, self, I2, I1, I3. */
export function memberBlock({ callerRole, can, target, action, newRole, members }: MemberBlockInput): BlockReason | null {
  const isAdmin = callerRole === 'admin';
  if (!can('member.manage')) {
    return { key: 'permissions.lockedField', keyValues: { action: 'permissions.member.manage.label' } };
  }
  if (target.isSelf && !isAdmin) return { key: 'team.block.self' };
  if (!isAdmin && ELEVATED.includes(target.role)) return { key: 'team.block.onlyAdminChanges' };
  if (action === 'setRole' && newRole && !isAdmin && ELEVATED.includes(newRole)) return { key: 'team.block.onlyAdminGrants' };
  const lastAdmin = target.role === 'admin' && target.status === 'active' && activeAdminCount(members) <= 1;
  const demotes = action === 'deactivate' || (action === 'setRole' && newRole !== undefined && newRole !== 'admin');
  if (lastAdmin && demotes) return { key: 'team.block.lastAdmin' };
  return null;
}

/** What to assign and unassign to go from `current` to `checked` (order kept, for a stable log). */
export function carrierDiff(current: readonly string[], checked: readonly string[]): { add: string[]; remove: string[] } {
  const now = new Set(current);
  const next = new Set(checked);
  return { add: checked.filter((id) => !now.has(id)), remove: current.filter((id) => !next.has(id)) };
}

/**
 * The toast after a carrier / dispatcher diff (CarrierAssignDialog, CarrierDispatchersDialog): only the
 * side that happened, so a pure addition does not read "2 added, 0 removed".
 */
export function assignResultMessage(
  added: number,
  removed: number,
): { key: 'team.assign.result' | 'team.assign.resultAdded' | 'team.assign.resultRemoved'; values: Record<string, number> } {
  if (removed === 0) return { key: 'team.assign.resultAdded', values: { count: added } };
  if (added === 0) return { key: 'team.assign.resultRemoved', values: { count: removed } };
  return { key: 'team.assign.result', values: { added, removed } };
}
